import { useState } from "react";
import { useNavigate } from "react-router-dom";
import InputField from "../components/InputField";
import SelectField from "../components/SelectField";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "../components/Button";
import Breadcrumbs from "../components/Breadcrumbs";
import { Modal } from "react-bootstrap";
import api from "../api/api";
import { useAuth } from "../context/AuthContext";
 
const WareHouseRequest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [bookingType, setBookingType] = useState("");
 
  // Full Day Booking
  const [fullStartDate, setFullStartDate] = useState(null);
  const [fullEndDate, setFullEndDate] = useState(null);
 
  // Hourly Booking
  const [hourlyDate, setHourlyDate] = useState(null);
  const [hourStartTime, setHourStartTime] = useState(null);
  const [hourEndTime, setHourEndTime] = useState(null);
 
  const [formValues, setFormValues] = useState({
    pincode: "",
    productName: "",
    hsnCode: "",
    storageType: "",
    packagingType: "",
    totalUnits: "",
    unitsType: "",
    tareWeight: "",
    length: "",
    breadth: "",
    height: "",
    volumetricWeight: "",
    productDescription: "",
    sku: "",
    specialRequirement: "",
    comment: "",
    hourlyType: "",
  });
 
  const [formErrors, setFormErrors] = useState({});
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
 
  // ------------------- Handlers -------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "pincode") {
      fetchWarehouses(value);
    }
  };
 
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFileName(selectedFile.name);
      setFile(selectedFile);
    } else {
      setFileName("");
      setFile(null);
    }
  };
 
  const handleDeleteFile = () => {
    setFileName("");
    setFile(null);
  };
 
  const handleOpenFile = () => {
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      window.open(fileUrl, "_blank");
    }
  };
 
  const fetchWarehouses = async (pincode) => {
    if (!pincode) {
      setWarehouses([]);
      return;
    }
    try {
      const response = await api.post("/warehouse/WhList", { pincode });
      setWarehouses(response.data.warehouses || []);
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      setWarehouses([]);
    }
  };
 
  const handleWarehouseSelect = (warehouse, isChecked) => {
    if (isChecked) {
      setSelectedWarehouses((prev) => [...prev, warehouse]);
    } else {
      setSelectedWarehouses((prev) =>
        prev.filter((wh) => wh.id !== warehouse.id),
      );
    }
  };
 
  const validate = () => {
    const errors = {};
    if (!formValues.pincode) {
      errors.pincode = "Pincode is required";
    }
    if (!formValues.productName)
      errors.productName = "Product Name is required";
    if (!formValues.hsnCode) errors.hsnCode = "HSN Code is required";
    if (!formValues.storageType)
      errors.storageType = "Storage Type is required";
    if (!formValues.packagingType)
      errors.packagingType = "Packaging Type is required";
    if (!formValues.totalUnits) errors.totalUnits = "Total Units is required";
    if (!formValues.unitsType) errors.unitsType = "Units Type is required";
    if (!formValues.tareWeight) errors.tareWeight = "Tare Weight is required";
    if (!formValues.length) errors.length = "Length is required";
    if (!formValues.breadth) errors.breadth = "Breadth is required";
    if (!formValues.height) errors.height = "Height is required";
    if (!formValues.volumetricWeight)
      errors.volumetricWeight = "Volumetric Weight is required";
    if (!formValues.productDescription)
      errors.productDescription = "Product Description is required";
    if (formValues.comment.length > 500)
      errors.comment = "Comment cannot exceed 500 characters";
 
    // Booking validation
    if (bookingType === "fullDay") {
      if (!fullStartDate) errors.fullStartDate = "Start Date is required";
      if (!fullEndDate) errors.fullEndDate = "End Date is required";
    }
 
    if (bookingType === "hourly") {
      if (!hourlyDate) errors.hourlyDate = "Date is required";
      if (!hourStartTime) errors.hourStartTime = "Start Time is required";
      if (!hourEndTime) errors.hourEndTime = "End Time is required";
      if (!formValues.hourlyType)
        errors.hourlyType = "Storage Type is required";
    }
 
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
 
  const handleSubmit = () => {
    if (validate()) {
      if (selectedWarehouses.length === 0) {
        alert("Please select at least one warehouse.");
        return;
      }
      setShowConfirm(true);
    }
  };
 
  const handleConfirm = async () => {
    setShowConfirm(false);
 
    if (!user || (!user.user_id && !user.id)) {
      alert("User not logged in. Please log in again.");
      return;
    }
 
    const warehouse_ids = selectedWarehouses.map((wh) => String(wh.id));
    const warehouse_owner_ids = selectedWarehouses.map((wh) =>
      String(wh.owner_user_id),
    );
    const warehouse_manager_ids = selectedWarehouses.map((wh) =>
      String(wh.manager_id),
    );
 
    const warehouse_names = selectedWarehouses.map((wh) => wh.name);
 
    // Prepare dates
    let start_date = null,
      end_date = null,
      start_time = null,
      end_time = null,
      hours = null,
      booking_duration = null;
    if (bookingType === "fullDay") {
      start_date = fullStartDate
        ? fullStartDate.toISOString().split("T")[0]
        : null;
      end_date = fullEndDate ? fullEndDate.toISOString().split("T")[0] : null;
      booking_duration = "fullDay";
    } else if (bookingType === "hourly") {
      start_date = hourlyDate ? hourlyDate.toISOString().split("T")[0] : null;
      start_time = hourStartTime
        ? hourStartTime.toTimeString().split(" ")[0]
        : null;
      end_time = hourEndTime ? hourEndTime.toTimeString().split(" ")[0] : null;
      hours = formValues.hourlyType;
      booking_duration = "hourly";
    }
 
    const formData = new FormData();
    formData.append("customer_id", user.user_id || user.id || "");
    formData.append("customer_name", user.full_name || "");
    formData.append("customer_email", user.email || "");
    formData.append("customer_phoneNo", user.phone_number || "");
    formData.append("customer_company_name", user.company_name || "");
    formData.append("product_name", formValues.productName);
    formData.append("HSN_code", formValues.hsnCode);
    formData.append(
      "total_unit",
      (Number.parseFloat(formValues.totalUnits) || 0).toString(),
    );
    formData.append("unit_type", formValues.unitsType);
    formData.append("storage_type", formValues.storageType);
    formData.append(
      "dead_weight",
      (Number.parseFloat(formValues.tareWeight) || 0).toString(),
    );
    formData.append(
      "length",
      (Number.parseFloat(formValues.length) || 0).toString(),
    );
    formData.append(
      "Breadth",
      (Number.parseFloat(formValues.breadth) || 0).toString(),
    );
    formData.append(
      "Height",
      (Number.parseFloat(formValues.height) || 0).toString(),
    );
    formData.append(
      "volumetric_weight",
      (Number.parseFloat(formValues.volumetricWeight) || 0).toString(),
    );
    formData.append("start_date", start_date || "");
    formData.append("end_date", end_date || "");
    formData.append("start_time", start_time || "");
    formData.append("end_time", end_time || "");
    formData.append("hours", hours || "");
    formData.append("booking_duration", booking_duration || "");
    formData.append("packaging_type", formValues.packagingType);
    formData.append("product_information", formValues.productDescription);
    formData.append("sku_or_item_code", formValues.sku || "");
    formData.append(
      "special_requirements",
      formValues.specialRequirement || "",
    );
    formData.append("pincode", formValues.pincode);
    formData.append("warehouse_ids", JSON.stringify(warehouse_ids));
    formData.append("warehouse_owner_ids", JSON.stringify(warehouse_owner_ids));
    formData.append(
      "warehouse_manager_ids",
      JSON.stringify(warehouse_manager_ids),
    );
    formData.append("warehouse_names", JSON.stringify(warehouse_names));
    formData.append("comments", formValues.comment || "");
    if (file) {
      formData.append("certificate", file);
    }
 
    try {
      await api.post("/warehouse/create-request", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      setShowSuccess(true);
    } catch (error) {
      console.error(
        "Error submitting request:",
        error.response?.data || error.message,
      );
      alert("Failed to submit request. Please try again.");
    }
  };
 
  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate("/warehouse-requests");
  };
  const containerTypeOptions = [
    {
      label: "Tare Weight",
      name: "tareWeight",
      placeholder: "Enter Tare Weight (in KGs)",
    },
    {
      label: "Length",
      name: "length",
      placeholder: "Enter Length (in cm)",
    },
    {
      label: "Breadth",
      name: "breadth",
      placeholder: "Enter Breadth (in CM's)",
    },
    {
      label: "Height",
      name: "height",
      placeholder: "Enter Height (in CM's)",
    },
    {
      label: "Volumetric Weight",
      name: "volumetricWeight",
      placeholder: "Enter Volumetric Weight (in KG's)",
    },
  ];
  return (
    <>
      <Breadcrumbs />
      <h4 className="section-title mb-3 fs-5 fw-semibold">
        Warehouse Preference
      </h4>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-6 col-md-4">
              <InputField
                clsname="required"
                label="Enter the Pincode"
                name="pincode"
                value={formValues.pincode}
                onChange={handleChange}
                placeholder="Enter Pincode"
                error={formErrors.pincode}
              />
            </div>
          </div>
          {warehouses.length > 0 && (
            <div className="mt-3">
              <h6>Select Warehouses:</h6>
              <div className="ovrflw overflow-x-auto">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Name</th>
                      <th>City</th>
                      <th>Base Rate</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warehouses.map((wh) => (
                      <tr key={wh.id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedWarehouses.some(
                              (selected) => selected.id === wh.id,
                            )}
                            onChange={(e) =>
                              handleWarehouseSelect(wh, e.target.checked)
                            }
                          />
                        </td>
                        <td>{wh.name}</td>
                        <td>{wh.location_city}</td>
                        <td>{wh.base_rate}</td>
                        <td>
                          {`${wh.address_line1 || ""} ${
                            wh.address_line2 || ""
                          }`.trim()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
 
      <h4 className="section-title mb-3 fs-5 fw-semibold">
        Select Booking Duration
      </h4>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <div className="d-flex flex-wrap gap-3">
            <label className="d-flex align-items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="bookingType"
                value="fullDay"
                checked={bookingType === "fullDay"}
                onChange={(e) => setBookingType(e.target.value)}
              />
              <span
                className="fw-semibold text-black"
                style={{ fontSize: "0.75rem" }}
              >
                Full Day Booking
              </span>
            </label>
 
            <label className="d-flex align-items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="bookingType"
                value="hourly"
                checked={bookingType === "hourly"}
                onChange={(e) => setBookingType(e.target.value)}
              />
              <span
                className="fw-semibold text-black"
                style={{ fontSize: "0.75rem" }}
              >
                Hourly Booking
              </span>
            </label>
          </div>
 
          {/* Full Day Booking */}
          {bookingType === "fullDay" && (
            <div className="row g-3 mt-4">
              <div className="col-md-4 col-sm-6">
                <label className="form-label fw-semibold required">
                  Start Date
                </label>
                <DatePicker
                  selected={fullStartDate}
                  onChange={(date) => setFullStartDate(date)}
                  className={`form-control ${formErrors.fullStartDate ? "is-invalid" : ""}`}
                  placeholderText="Select Start Date"
                  dateFormat="dd/MM/yyyy"
                  minDate={new Date()}
                />
                {formErrors.fullStartDate && (
                  <div className="text-danger small">
                    {formErrors.fullStartDate}
                  </div>
                )}
              </div>
 
              <div className="col-md-4 col-sm-6">
                <label className="form-label fw-semibold required">
                  End Date
                </label>
                <DatePicker
                  selected={fullEndDate}
                  onChange={(date) => setFullEndDate(date)}
                  className={`form-control ${formErrors.fullEndDate ? "is-invalid" : ""}`}
                  placeholderText="Select End Date"
                  dateFormat="dd/MM/yyyy"
                  minDate={fullStartDate || new Date()}
                />
 
                {formErrors.fullEndDate && (
                  <div className="text-danger small">
                    {formErrors.fullEndDate}
                  </div>
                )}
              </div>
            </div>
          )}
 
          {/* Hourly Booking */}
          {bookingType === "hourly" && (
            <>
              <div className="row g-3 mb-3 mt-4">
                <div className="col-sm-6 col-md-4">
                  <SelectField
                    label="Hour Type"
                    options={["4", "8", "12", "16", "20"]}
                    name="hourlyType"
                    value={formValues.hourlyType}
                    clsname="required"
                    onChange={(value) =>
                      setFormValues((prev) => ({
                        ...prev,
                        hourlyType: value,
                      }))
                    }
                    placeholder="Select Hour"
                  />
                  {formErrors.hourlyType && (
                    <div className="text-danger small">
                      {formErrors.hourlyType}
                    </div>
                  )}
                </div>
              </div>
 
              <div className="row g-3">
                <div className="col-md-4 col-sm-6">
                  <label className="form-label fw-semibold required">
                    Select Date
                  </label>
                  <DatePicker
                    selected={hourlyDate}
                    onChange={(date) => setHourlyDate(date)}
                    className={`form-control ${formErrors.hourlyDate ? "is-invalid" : ""}`}
                    placeholderText="Select Date"
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                  />
                  {formErrors.hourlyDate && (
                    <div className="text-danger small">
                      {formErrors.hourlyDate}
                    </div>
                  )}
                </div>
 
                <div className="col-sm-6 col-md-4">
                  <label className="form-label fw-semibold required">
                    Start Time
                  </label>
                  <DatePicker
                    selected={hourStartTime}
                    onChange={(time) => setHourStartTime(time)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Start Time"
                    dateFormat="hh:mm aa"
                    className={`form-control ${formErrors.hourStartTime ? "is-invalid" : ""}`}
                    placeholderText="Select Start Time"
                  />
                  {formErrors.hourStartTime && (
                    <div className="text-danger small">
                      {formErrors.hourStartTime}
                    </div>
                  )}
                </div>
 
                <div className="col-sm-6 col-md-4">
                  <label className="form-label fw-semibold required">
                    End Time
                  </label>
                  <DatePicker
                    selected={hourEndTime}
                    onChange={(time) => setHourEndTime(time)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="End Time"
                    dateFormat="hh:mm aa"
                    className={`form-control ${formErrors.hourEndTime ? "is-invalid" : ""}`}
                    placeholderText="Select End Time"
                  />
                  {formErrors.hourEndTime && (
                    <div className="text-danger small">
                      {formErrors.hourEndTime}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
 
      <h4 className="section-title mb-3 fs-5 fw-semibold">
        Storage Requirement
      </h4>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-6 col-md-4">
              <InputField
                label="Product Name"
                name="productName"
                clsname="required"
                value={formValues.productName}
                onChange={handleChange}
                placeholder="Enter Product Name"
                error={formErrors.productName}
              />
            </div>
 
            <div className="col-sm-6 col-md-4">
              <InputField
                label="HSN Code"
                name="hsnCode"
                clsname="required"
                value={formValues.hsnCode}
                onChange={handleChange}
                placeholder="Enter HSN Code"
                error={formErrors.hsnCode}
              />
            </div>
 
            <div className="col-sm-6 col-md-4">
              <SelectField
                label="Storage Type"
                clsname="required"
                options={[
                  "Hazardous",
                  "Non Hazardous",
                  "Temperature Control",
                  "Moisture Control",
                  "Heavy Duty",
                  "Over Dimensional Cargo",
                ]}
                name="storageType"
                value={formValues.storageType}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, storageType: value }))
                }
                placeholder="Select Storage Type"
                error={formErrors.storageType}
              />
              {formErrors.storageType && (
                <div className="text-danger small">
                  {formErrors.storageType}
                </div>
              )}
            </div>
 
            <div className="col-sm-6 col-md-4">
              <InputField
                clsname="required"
                label="Packaging Type"
                name="packagingType"
                value={formValues.packagingType}
                onChange={handleChange}
                placeholder="Enter Packaging Type"
                error={formErrors.packagingType}
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <InputField
                clsname="required"
                label="Total Units"
                name="totalUnits"
                value={formValues.totalUnits}
                onChange={handleChange}
                placeholder="Enter Total Units"
                error={formErrors.totalUnits}
              />
            </div>
            <div className="col-sm-6 col-md-4">
              <SelectField
                label="Units Type"
                name="unitsType"
                value={formValues.unitsType}
                onChange={(value) =>
                  setFormValues((prev) => ({ ...prev, unitsType: value }))
                }
                error={formErrors.unitsType}
                options={[
                  { value: "pallet", label: "Pallets" },
                  { value: "cubic_feet", label: "Cubic Feet" },
                  { value: "cubic_meters", label: "Cubic Meters" },
                ]}
              />
            </div>
 
            {containerTypeOptions.map((field) => (
              <div className="col-sm-6 col-md-4" key={field.name}>
                <InputField
                  clsname="required"
                  label={field.label}
                  name={field.name}
                  value={formValues[field.name]}
                  onChange={handleChange}
                  placeholder={field.placeholder}
                  error={formErrors[field.name]}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
 
      <h4 className="section-title mb-3 fs-5 fw-semibold">Inventory Details</h4>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-6 col-md-4">
              <InputField
                clsname="required"
                label="Product Description"
                name="productDescription"
                value={formValues.productDescription}
                onChange={handleChange}
                placeholder="Enter Product Description"
                error={formErrors.productDescription}
              />
            </div>
 
            <div className="col-sm-6 col-md-4">
              <InputField
                label="SKU or Item Code (optional)"
                name="sku"
                value={formValues.sku}
                onChange={handleChange}
                placeholder="Enter SKU"
              />
            </div>
 
            <div className="col-sm-6 col-md-4">
              <InputField
                label="Special Requirement (optional)"
                name="specialRequirement"
                value={formValues.specialRequirement}
                onChange={handleChange}
                placeholder="Enter Special Requirement"
              />
            </div>
          </div>
        </div>
      </div>
 
      <h4 className="section-title mb-3 fs-5 fw-semibold">
        Additional Information
      </h4>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <div className="mb-3">
            <InputField
              label="Comment"
              name="comment"
              placeholder="Let us know if you have any specific requirement"
              value={formValues.comment}
              onChange={handleChange}
              maxLength={500}
              error={formErrors.comment}
            />
            <div className="text-end small text-muted">
              {formValues.comment.length}/500
            </div>
          </div>
 
          <div className="d-flex align-items-center gap-3 mb-3">
            <label
              htmlFor="product-certifications"
              className="d-flex align-items-center gap-2 cursor-pointer text-primary fw-semibold"
            >
              <UploadFileIcon />
              <span>Upload Product Certifications</span>
            </label>
            <input
              type="file"
              id="product-certifications"
              className="d-none"
              onChange={handleFileChange}
            />
          </div>
 
          {fileName && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <span
                className="text-success small cursor-pointer text-decoration-underline"
                onClick={handleOpenFile}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleOpenFile();
                  }
                }}
              >
                {fileName}
              </span>
              <DeleteIcon
                style={{ cursor: "pointer" }}
                onClick={handleDeleteFile}
              />
            </div>
          )}
        </div>
      </div>
 
      <div className="d-flex justify-content-end mt-3">
        <Button text="Submit" onClick={handleSubmit} />
      </div>
 
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to submit this warehouse request?
        </Modal.Body>
        <Modal.Footer>
          <Button
            text="Cancel"
            variant="secondary"
            onClick={() => setShowConfirm(false)}
          />
          <Button text="Confirm" variant="primary" onClick={handleConfirm} />
        </Modal.Footer>
      </Modal>
 
      <Modal show={showSuccess} onHide={handleSuccessClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Success</Modal.Title>
        </Modal.Header>
        <Modal.Body>Warehouse request submitted successfully!</Modal.Body>
        <Modal.Footer>
          <Button text="OK" variant="success" onClick={handleSuccessClose} />
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default WareHouseRequest;